import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { getAllTenantProvisioningEvents } from '../api/tenantProvisioningApi';
import { DataTable } from '../components/DataTable';
import { ErrorState } from '../components/ErrorState';
import { LoadingState } from '../components/LoadingState';
import { PageShell } from '../components/PageShell';
import { formatProvisioningEventMessage, safeTranslate } from '../utils/systemLabels';

export function TenantProvisioningEventsPage() {
  const { t } = useTranslation(['tenantProvisioning', 'common']);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getAllTenantProvisioningEvents({ per_page: 100 });
      setRows(response.data || []);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const columns = useMemo(() => [
    { id: 'tenant_id', label: t('tenantProvisioning:events.columns.tenant'), render: (row) => row.tenant_id ? <RouterLink to={`/tenants/${row.tenant_id}`}>{t('tenantProvisioning:dashboard.tenant_link', { id: row.tenant_id })}</RouterLink> : t('common:empty_value') },
    {
      id: 'event_type',
      label: t('tenantProvisioning:events.columns.event'),
      render: (row) => safeTranslate(t, `tenantProvisioning:event_types.${row.event_type}`, row.event_type),
    },
    {
      id: 'message',
      label: t('tenantProvisioning:events.columns.message'),
      render: (row) => formatProvisioningEventMessage(row) || t('common:empty_value'),
    },
    { id: 'created_at', label: t('tenantProvisioning:events.columns.at') },
  ], [t]);

  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={t('tenantProvisioning:events.load_failed')} onRetry={load} />;

  return (
    <PageShell title={t('tenantProvisioning:events.title')} subtitle={t('tenantProvisioning:events.subtitle')}>
      <DataTable columns={columns} rows={rows} rowKey="id" emptyMessage={t('tenantProvisioning:events.no_events')} />
    </PageShell>
  );
}
